import { CodeSnippet } from '../model/types';

export const codeSnippets: Record<string, Record<string, CodeSnippet | CodeSnippet[]>> = {
  '1': {
    '1.2': {
      section: '1.2',
      name: 'Pauli & Basic Gates',
      code: `import numpy as np

# Basis vectors
zero = np.array([1,0], complex)
one  = np.array([0,1], complex)

# Identity and familiar single-qubit gates
I = np.eye(2, dtype=complex)
X = np.array([[0,1],[1,0]], complex)          # NOT
Y = np.array([[0,-1j],[1j,0]], complex)
Z = np.array([[1,0],[0,-1]], complex)
H = (1/np.sqrt(2))*np.array([[1,1],[1,-1]], complex)

def is_unitary(U):
    return np.allclose(U.conj().T @ U, I)

for name, gate in [('X',X), ('Y',Y), ('Z',Z), ('H',H)]:
    print(f"{name} is unitary? {is_unitary(gate)}")

print("X|0> =", X @ zero)                    # should be |1>
plus  = H @ zero                             # (|0>+|1>)/√2
minus = H @ one                              # (|0>-|1>)/√2
print("|+> =", plus)
print("Z|+> flips its phase →", Z @ plus)
    `,
    },
    '1.3': {
      section: '1.3',
      name: 'Stern-Gerlach Spin Splitter',
      code: `import numpy as np

up   = np.array([1,0], complex)          # |↑_z>
Y = np.array([[0,-1j],[1j,0]], complex)  # Pauli-Y
I = np.eye(2, dtype=complex)

def R_y(theta):
    """Rotation about y-axis by θ (right-hand rule)."""
    return np.cos(theta/2)*I - 1j*np.sin(theta/2)*Y

print("🧲 STERN-GERLACH SPIN SPLITTER EXPERIMENT")
print("=" * 45)
print("📖 Historical context: This 1922 experiment proved")
print("   that quantum spin is quantized and probabilistic!")

print(f"\\n1️⃣  Initial state: Electron with spin-up in z-direction")
print(f"    |ψ₀⟩ = |↑_z⟩ = {up}")
print(f"    🧭 Spin vector points UP along z-axis")

theta = np.pi/3                          # 60°
print(f"\\n2️⃣  Rotating spin around y-axis by {theta*180/np.pi:.0f}°")
print(f"    🔄 This changes the spin direction in space")

psi = R_y(theta) @ up                    # rotate spin
print(f"    Rotation matrix R_y({theta*180/np.pi:.0f}°):")
rotation_matrix = R_y(theta)
print(f"    {rotation_matrix.round(3)}")
print(f"\\n    New state after rotation:")
print(f"    |ψ⟩ = {psi.round(3)}")

p_up   = abs(psi[0])**2                  # P(↑_z)
p_down = abs(psi[1])**2                  # P(↓_z)

print(f"\\n3️⃣  Stern-Gerlach magnet measurement in z-direction:")
print(f"    🧲 Magnetic field gradient separates spin-up/down")
print(f"    📊 Quantum probabilities (Born rule):")
print(f"    ")
print(f"    P(↑_z) = |⟨↑_z|ψ⟩|² = {p_up:.3f} = {p_up*100:.1f}%")
print(f"    P(↓_z) = |⟨↓_z|ψ⟩|² = {p_down:.3f} = {p_down*100:.1f}%")

# Verify the sin² rule
theoretical_p_down = np.sin(theta/2)**2
print(f"\\n4️⃣  Theoretical verification:")
print(f"    📐 Sin² rule: P(↓_z) = sin²(θ/2) = sin²({theta*180/np.pi:.0f}°/2)")
print(f"    📐 sin²({theta*180/np.pi/2:.0f}°) = {theoretical_p_down:.3f}")
print(f"    ✅ Matches our calculation: {abs(p_down - theoretical_p_down) < 1e-10}")

print(f"\\n🎯 EXPERIMENTAL OUTCOME:")
if p_up > p_down:
    print(f"    🔵 Electron more likely to deflect UP ({p_up*100:.1f}% chance)")
else:
    print(f"    🔴 Electron more likely to deflect DOWN ({p_down*100:.1f}% chance)")

print(f"\\n💡 Physical interpretation:")
print(f"    • Original spin pointed up (100% up, 0% down)")
print(f"    • After {theta*180/np.pi:.0f}° rotation: now tilted in space")
print(f"    • Measurement forces collapse to either ↑ or ↓")
print(f"    • Probabilities depend on overlap with measurement basis")
print(f"\\n🌟 This proves quantum mechanics is fundamentally probabilistic!")

print(f"\\nTotal probability check: {p_up + p_down:.6f} (must equal 1.0)") 
    `,
    },
    '1.4': [
      {
        section: '1.4',
        name: 'Single-Qubit Superposition',
        code: `import numpy as np                       # only dependency

# |0> = [1,0] , |1> = [0,1]
zero = np.array([1, 0], complex)
one  = np.array([0, 1], complex)

# Build an arbitrary qubit |ψ> = α|0> + β|1>
alpha = 1/np.sqrt(2)      # try changing these two numbers
beta  = 1/np.sqrt(2)
psi = alpha*zero + beta*one

# Hadamard gate: (|0>→|+>, |1>→|->)
H = (1/np.sqrt(2))*np.array([[1, 1],
                            [1,-1]], complex)

psi_after = H @ psi        # apply the gate

# Born rule probabilities
p0 = abs(psi_after[0])**2
p1 = abs(psi_after[1])**2

print("State after H :", psi_after)
print(f"Prob(0) = {p0:.3f} , Prob(1) = {p1:.3f}")
print("Probabilities add to", round(p0+p1, 10))      # sanity-check: must be 1
      `,
      },
      {
        section: '1.4',
        name: 'Bloch-Sphere Coordinates',
        code: `import numpy as np
# Optional visual block uses matplotlib; keep it commented if you wish
# import matplotlib.pyplot as plt

def bloch_xyz(theta, phi):
    """Convert polar angles to Bloch-sphere (x,y,z)."""
    x = np.sin(theta)*np.cos(phi)
    y = np.sin(theta)*np.sin(phi)
    z = np.cos(theta)
    return x, y, z

# Pick angles (in radians)
theta = np.pi/3            # 60° down from north pole
phi   = np.pi/4            # 45° around the equator

# Corresponding state vector
alpha = np.cos(theta/2)
beta  = np.exp(1j*phi)*np.sin(theta/2)
psi = np.array([alpha, beta])

x, y, z = bloch_xyz(theta, phi)
print("State |ψ> =", psi.round(3))
print(f"Bloch point ≈ ({x:.2f}, {y:.2f}, {z:.2f})")

"""
# Uncomment to see it on a sphere
fig = plt.figure()
ax = fig.add_subplot(projection='3d')
# Wireframe sphere (just for context)
u,v = np.mgrid[0:2*np.pi:36j, 0:np.pi:18j]
ax.plot_wireframe(np.cos(u)*np.sin(v),
                    np.sin(u)*np.sin(v),
                    np.cos(v), lw=0.3)
ax.scatter(x, y, z, s=80)
plt.show()
"""
      `,
      },
    ],
    '1.5': {
      section: '1.5',
      name: 'Projective Measurement Monte-Carlo',
      code: `import numpy as np

# Build any qubit you like
alpha = 0.6 + 0j
beta  = np.sqrt(1 - abs(alpha)**2)
psi = np.array([alpha, beta])

probs = abs(psi)**2             # exact probabilities
shots = 10_000
counts = {'0':0, '1':0}

rng = np.random.default_rng()
for _ in range(shots):
    outcome = rng.choice([0,1], p=probs)
    counts[str(outcome)] += 1

print("Theory  :", probs)
print("Observed:", {k: v/shots for k,v in counts.items()})
    `,
    },
    '1.6': {
      section: '1.6',
      name: 'Toy Quantum Teleportation',
      code: `import numpy as np
k = np.kron                           # helper

# Basis & gates
zero = np.array([1,0], complex)
one  = np.array([0,1], complex)
I = np.eye(2, dtype=complex)
H = (1/np.sqrt(2))*np.array([[1,1],[1,-1]], complex)
X = np.array([[0,1],[1,0]], complex)
Z = np.array([[1,0],[0,-1]], complex)
CNOT = np.array([[1,0,0,0],
                [0,1,0,0],
                [0,0,0,1],
                [0,0,1,0]], complex)   # control-0, target-1

print("🚀 QUANTUM TELEPORTATION PROTOCOL")
print("=" * 40)

# 1) Unknown state on qubit A
alpha, beta = 0.6, 0.8j
psi_A = alpha*zero + beta*one
print(f"1️⃣  Alice's unknown state: |ψ⟩ = {alpha}|0⟩ + {beta}|1⟩")
print(f"    Vector form: {psi_A}")

# 2) Shared Bell pair between B & C
bell_BC = (1/np.sqrt(2))*(k(zero,zero) + k(one,one))
print(f"\\n2️⃣  Shared Bell pair |Φ+⟩ = (|00⟩+|11⟩)/√2")
print(f"    Between Bob (B) and Charlie (C)")

# 3-qubit composite state |ψ_A>⊗|Φ+_BC>
state = np.kron(psi_A, bell_BC)
print(f"\\n3️⃣  Initial 3-qubit state |ψ_A⟩⊗|Φ+_BC⟩:")
print(f"    State vector: {state.round(3)}")

# 3) Alice's CNOT (A→B) then H on A
print(f"\\n4️⃣  Alice applies quantum operations:")
state = np.kron(CNOT, I) @ state
print(f"    ✓ CNOT gate (Alice→Bob)")
state = np.kron(H, np.eye(4, dtype=complex)) @ state
print(f"    ✓ Hadamard gate on Alice's qubit")

# 4) Pretend Alice measured 00 (one of four equally-likely results)
print(f"\\n5️⃣  Alice measures her qubits and gets: |00⟩")
print(f"    (This happens 25% of the time)")
bob_state = state[[0,1]]                # amplitudes where A,B = 00 (indices 0,1 = |000⟩,|001⟩)
bob_state /= np.linalg.norm(bob_state)  # renormalise
print(f"    Bob's collapsed state: {bob_state.round(3)}")

# Outcome 00 → Bob does nothing; compare with original
fidelity = abs(np.vdot(psi_A, bob_state))**2
print(f"\\n6️⃣  Alice tells Bob: 'I measured 00, do nothing!'")
print(f"    Bob's final state: {bob_state.round(3)}")
print(f"    Original state:    {psi_A.round(3)}")
print(f"\\n🎯 TELEPORTATION SUCCESS!")
print(f"    Fidelity = {fidelity:.4f} (1.0000 = perfect!)")

if fidelity > 0.999:
    print("    ✅ Alice's quantum state perfectly teleported to Bob!")
else:
    print("    ❌ Something went wrong...")

print("\\n💡 Note: Bob now has Alice's exact quantum state,")
print("    while Alice's original qubit is destroyed by measurement.")
    `,
    },
    '1.7': {
      section: '1.7',
      name: 'Making a Bell State',
      code: `import numpy as np
kron = np.kron                      # alias for readability

# |00> start state
zero = np.array([1,0], complex)
one  = np.array([0,1], complex)
state = kron(zero, zero)            # 2-qubit column vector

H = (1/np.sqrt(2))*np.array([[1,1],[1,-1]], complex)
I = np.eye(2, dtype=complex)
# Controlled-NOT (control qubit 0 → target qubit 1)
CNOT = np.array([[1,0,0,0],
                [0,1,0,0],
                [0,0,0,1],
                [0,0,1,0]], complex)

state = kron(H, I) @ state          # apply H on qubit 0
state = CNOT @ state                # then CNOT

print("Ideal |Φ+> = (|00>+|11>)/√2")
print("Our vector :", state.round(3))

# Quick correlation check
probs = abs(state)**2               # order: 00,01,10,11
print("Probabilities:", probs)
    `,
    },
  },

  '3': {
    '1.1.3': [
      {
        section: '1.1.3',
        name: 'Three-Qubit Quantum Fourier Transform',
        code: `# ================================================================
# forward + inverse → identity
# ================================================================
import numpy as np
import math

print("🌊 QUANTUM FOURIER TRANSFORM (QFT)")
print("=" * 45)
print("🎯 Goal: Transform between computational and frequency basis")
print("📐 Mathematical magic: Discrete Fourier Transform on quantum states!")

n = 3
N = 2**n

print(f"\\n1️⃣  QFT SETUP:")
print(f"    📏 Number of qubits: n = {n}")
print(f"    🌌 Hilbert space dimension: N = 2^{n} = {N}")
print(f"    🔄 Transform: |x⟩ → (1/√N) Σ ω^(xk) |k⟩")
print(f"    🧮 Primitive root: ω = e^(2πi/N) = e^(2πi/{N})")

# ---------- 1. Build the QFT matrix -----------------------------
ω = np.exp(2j * math.pi / N)
print(f"\\n    Complex 8th root: ω = {ω:.3f}")
print(f"    Powers of ω: ω^0={ω**0:.3f}, ω^1={ω**1:.3f}, ω^2={ω**2:.3f}, ...")

QFT = np.zeros((N, N), complex)
for j in range(N):
    for k in range(N):
        QFT[j, k] = ω**(j*k) / np.sqrt(N)

print(f"\\n2️⃣  QFT MATRIX CONSTRUCTION:")
print(f"    📊 Building {N}×{N} QFT matrix: QFT[j,k] = ω^(jk)/√{N}")
print(f"    🎭 Matrix elements (first 4×4 block):")
for i in range(4):
    row_str = "    "
    for j in range(4):
        elem = QFT[i,j]
        if abs(elem.imag) < 1e-10:
            row_str += f"{elem.real:+.3f}     "
        else:
            row_str += f"{elem.real:+.3f}{elem.imag:+.3f}j "
    print(row_str)

# ---------- 2. Choose an input basis state |5> = |101> ----------
state = np.zeros(N, complex)
state[5] = 1

print(f"\\n3️⃣  INPUT STATE:")
print(f"    🎲 Chosen input: |5⟩ = |101⟩ (computational basis)")
print(f"    📊 State vector: {state}")
print(f"    💡 This is a sharp 'spike' in computational space")

# ---------- 3. Forward QFT: computational → frequency ----------
print(f"\\n4️⃣  FORWARD QFT (Time → Frequency):")
ψ_k = QFT @ state
print(f"    🌊 Applying QFT matrix to |5⟩...")
print(f"    📈 Result in frequency domain:")

for k in range(N):
    amplitude = ψ_k[k]
    prob = abs(amplitude)**2
    phase = np.angle(amplitude) * 180 / np.pi
    print(f"    |{k:03b}⟩: amp = {amplitude:.3f}, |amp|² = {prob:.4f}, phase = {phase:+.1f}°")

print(f"\\n    🎵 Notice: All amplitudes have equal magnitude!")
print(f"    📐 Phase pattern encodes the original state information")

# ---------- 4. Inverse QFT: frequency → computational ----------
print(f"\\n5️⃣  INVERSE QFT (Frequency → Time):")
print(f"    🔄 Applying QFT† (conjugate transpose) to get back...")
state_back = QFT.conj().T @ ψ_k           # QFT† = inverse

print(f"    📊 Recovered state:")
for i in range(N):
    if abs(state_back[i]) > 1e-10:
        print(f"    |{i:03b}⟩: {state_back[i]:.6f}")

# ---------- 5. Verification: QFT · QFT† = I -------------------
print(f"\\n6️⃣  VERIFICATION:")
recovered_amplitude = state_back[5]
fidelity = abs(recovered_amplitude)**2
norm_diff = np.linalg.norm(state_back - state)

print(f"    🎯 Original state: |5⟩ = |101⟩")
print(f"    📤 Recovered amplitude at |5⟩: {recovered_amplitude:.6f}")
print(f"    📈 Fidelity: |⟨5|ψ_recovered⟩|² = {fidelity:.8f}")
print(f"    📏 Norm difference: ||ψ_recovered - ψ_original|| = {norm_diff:.2e}")

if fidelity > 0.999999:
    print(f"    ✅ PERFECT RECOVERY! QFT · QFT† = I verified!")
else:
    print(f"    ❌ Something went wrong...")

# ---------- 6. Mathematical insight ----------------------------
print(f"\\n💡 MATHEMATICAL INSIGHT:")
print(f"    🌊 QFT is the quantum version of discrete Fourier transform")
print(f"    🔄 Unitary operation: QFT† · QFT = I (preserves probability)")
print(f"    📐 Transforms sharp computational states → spread frequency states")
print(f"    🎵 Extracts periodic structure from quantum states")

print(f"\\n🚀 APPLICATIONS:")
print(f"    • Shor's algorithm (period finding for factoring)")
print(f"    • Quantum phase estimation")
print(f"    • Solving linear systems")
print(f"    • Hidden subgroup problems")

print(f"\\n🧮 Computational complexity:")
print(f"    🐌 Classical FFT: O(N log N) = O({N} × {n}) operations")
print(f"    ⚡ Quantum QFT: O(n²) = O({n}²) = O({n*n}) quantum gates")
print(f"    📊 Exponential speedup for large n!")

print(f"\\n🌟 The QFT is a cornerstone of quantum computing!")
print(f"    It enables exponential quantum advantages in many algorithms.")
    `,
      },
      {
        section: '1.1.3',
        name: 'Circuit Depth vs. Noise Fidelity Toy',
        code: `"""
🎯 NISQ-Era Challenge: Circuit Depth vs. Noise Fidelity

This demonstration shows the fundamental limitation of near-term quantum devices:
as circuit depth increases, noise accumulates exponentially, destroying quantum
coherence and making deep quantum algorithms impractical on current hardware.

Key Concepts:
• Depolarizing noise: Random errors that mix quantum states
• Fidelity decay: Exponential loss of quantum information
• NISQ constraint: Why we need shallow, variational algorithms

Real-world impact: This is why we can't run Shor's algorithm on large numbers
yet - the circuits are too deep for current noisy quantum processors!
"""

import numpy as np
import random
import matplotlib.pyplot as plt

print("🔬 QUANTUM NOISE ACCUMULATION EXPERIMENT")
print("=" * 60)
print("📖 Demonstrating the fundamental NISQ-era challenge:")
print("   How quantum noise accumulates with circuit depth")
print("   Why deep quantum algorithms fail on current hardware")

def depolarise(ψ, eps):
    """
    Depolarizing noise model: With probability eps, replace state 
    by maximally mixed (random) component.
    
    This models the dominant noise in quantum processors:
    - Thermal relaxation (T1 decay)
    - Dephasing (T2 decay)  
    - Gate infidelities
    """
    return (1-eps)*ψ + eps*np.ones_like(ψ)/len(ψ)

# ========== 1. Problem Setup ==========================================

print(f"\\n1️⃣  EXPERIMENTAL SETUP:")
n = 3
N = 2**n
print(f"    🎯 System: {n} qubits ({N} basis states)")
print(f"    🚀 Initial state: |000⟩ (perfect quantum state)")

# Start in |000>
state_ideal = np.zeros(N, complex)
state_ideal[0] = 1
state_noisy = state_ideal.copy()

print(f"    🔄 Quantum Gate: SWAP operation")
print(f"       • Swaps |001⟩ ↔ |010⟩ states")
print(f"       • Represents typical 2-qubit quantum gate")

# Simple 2-qubit gate = SWAP (here as permutation matrix)
SWAP = np.eye(N, dtype=complex)
SWAP[[1,2]] = SWAP[[2,1]]              # just swap |001> and |010>

depth = 20
eps_per_gate = 0.02
print(f"    📏 Circuit depth: {depth} gates")
print(f"    🔥 Noise level: {eps_per_gate:.1%} depolarizing per gate")
print(f"       (Typical for current quantum processors)")

# ========== 2. Noise Evolution Simulation =============================

print(f"\\n2️⃣  FIDELITY DECAY SIMULATION:")
print(f"    {'Depth':<6} {'Fidelity':<10} {'Loss':<8} {'Status':<25}")
print(f"    {'-'*6} {'-'*10} {'-'*8} {'-'*25}")

fidelities = []
depths = []

for d in range(1, depth+1):
    # Apply ideal gate
    state_ideal = SWAP @ state_ideal
    
    # Apply noisy gate
    state_noisy = SWAP @ state_noisy
    state_noisy = depolarise(state_noisy, eps_per_gate)  # noise hit
    
    # Calculate fidelity
    fidelity = abs(np.vdot(state_ideal, state_noisy))**2
    fidelities.append(fidelity)
    depths.append(d)
    
    # Print periodic updates
    if d == 1 or d % 5 == 0 or d == depth:
        loss = 1 - fidelity
        
        if fidelity > 0.9:
            status = "✅ Excellent"
        elif fidelity > 0.7:
            status = "⚠️  Degraded"
        elif fidelity > 0.5:
            status = "❌ Poor"
        else:
            status = "💀 Failed"
            
        print(f"    {d:<6} {fidelity:<10.3f} {loss:<8.1%} {status}")

# ========== 3. Mathematical Analysis ==================================

print(f"\\n3️⃣  MATHEMATICAL ANALYSIS:")

# Calculate theoretical exponential decay
theoretical_fidelity = [(1 - eps_per_gate)**d for d in depths]
actual_final = fidelities[-1]
theoretical_final = theoretical_fidelity[-1]

print(f"    📊 Final Results (depth {depth}):")
print(f"       • Actual fidelity: {actual_final:.4f}")
print(f"       • Theoretical: {theoretical_final:.4f}")
print(f"       • Information loss: {1-actual_final:.1%}")

# Find depth where fidelity drops below 50%
threshold_depth = None
for i, f in enumerate(fidelities):
    if f < 0.5:
        threshold_depth = i + 1
        break

if threshold_depth:
    print(f"    ⚠️  Circuit becomes unreliable at depth {threshold_depth}")
else:
    print(f"    ✅ Circuit remains >50% reliable throughout")

# ========== 4. Visualization ===========================================

print(f"\\n4️⃣  CREATING VISUALIZATION...")

plt.figure(figsize=(12, 8))

# Main plot: Fidelity vs Depth
plt.subplot(2, 2, 1)
plt.plot(depths, fidelities, 'b-o', linewidth=2, markersize=4, label='Actual')
plt.plot(depths, theoretical_fidelity, 'r--', linewidth=2, label='Theoretical (1-ε)^d')
plt.axhline(y=0.5, color='orange', linestyle=':', label='50% threshold')
plt.xlabel('Circuit Depth')
plt.ylabel('Fidelity')
plt.title('Quantum Fidelity Decay with Noise')
plt.legend()
plt.grid(True, alpha=0.3)

# Log plot to show exponential decay
plt.subplot(2, 2, 2)
plt.semilogy(depths, fidelities, 'b-o', linewidth=2, markersize=4, label='Actual')
plt.semilogy(depths, theoretical_fidelity, 'r--', linewidth=2, label='Theoretical')
plt.xlabel('Circuit Depth')
plt.ylabel('log(Fidelity)')
plt.title('Exponential Decay (Log Scale)')
plt.legend()
plt.grid(True, alpha=0.3)

# Noise accumulation
plt.subplot(2, 2, 3)
error_rates = [1 - f for f in fidelities]
plt.plot(depths, error_rates, 'r-s', linewidth=2, markersize=4)
plt.xlabel('Circuit Depth')
plt.ylabel('Error Rate')
plt.title('Error Accumulation')
plt.grid(True, alpha=0.3)

# State overlap visualization for last few depths
plt.subplot(2, 2, 4)
recent_depths = depths[-10:]
recent_fidelities = fidelities[-10:]
plt.bar(recent_depths, recent_fidelities, alpha=0.7, color='skyblue')
plt.axhline(y=0.5, color='red', linestyle='--', label='Failure threshold')
plt.xlabel('Circuit Depth')
plt.ylabel('Fidelity')
plt.title('Recent Performance')
plt.legend()

plt.tight_layout()
plt.savefig('noise_fidelity_analysis.png', dpi=300, bbox_inches='tight')
plt.show()

# ========== 5. NISQ Implications =====================================

print(f"\\n5️⃣  NISQ-ERA IMPLICATIONS:")
print(f"    🏭 Current quantum processors have:")
print(f"       • Gate errors: ~0.1-1% per operation")
print(f"       • Coherence times: ~10-100 μs")
print(f"       • Maximum reliable depth: ~50-200 gates")

print(f"\\n    📈 Scaling Challenge:")
if threshold_depth:
    extrapolated_1000 = (1 - eps_per_gate)**(1000)
    print(f"       • At depth 1000: fidelity ≈ {extrapolated_1000:.2e}")
    print(f"       • Essentially zero - complete information loss!")
else:
    print(f"       • Even at depth 20: significant degradation")

print(f"\\n    💡 Why This Matters:")
print(f"       • Shor's algorithm: needs ~8n³ gates for n-bit numbers")
print(f"       • RSA-2048: would need ~10¹⁰ gates!")
print(f"       • Current hardware: reliable for ~100 gates")
print(f"       • Gap: 8 orders of magnitude!")

print(f"\\n    🔧 NISQ-Era Solutions:")
print(f"       ✓ Variational algorithms (VQE, QAOA)")
print(f"       ✓ Shallow circuit depths (<100 gates)")
print(f"       ✓ Error mitigation techniques")
print(f"       ✓ Hybrid classical-quantum algorithms")

# ========== 6. Real-World Context ==================================

print(f"\\n6️⃣  REAL-WORLD CONTEXT:")
print(f"    🏢 Current quantum advantage:")
print(f"       • Google Sycamore: 53 qubits, ~20 cycles")
print(f"       • IBM: up to 1000+ qubits, shallow circuits")
print(f"       • Limited to specific, carefully designed problems")

print(f"\\n    🔮 Future prospects:")
print(f"       • Error correction: requires ~1000 physical qubits per logical")
print(f"       • Fault-tolerant era: 2030s-2040s estimated")
print(f"       • Until then: NISQ algorithms are our best bet")

print(f"\\n    🎯 Key Takeaway:")
print(f"       Quantum noise fundamentally limits current quantum computers.")
print(f"       This is why variational algorithms dominate near-term applications!")

print(f"\\n" + "="*60)
print(f"🎓 EXPERIMENT COMPLETE: Noise accumulation demonstrated!")
print(f"💡 Understanding achieved: Why deep circuits fail in NISQ era!")
print(f"🚀 Next steps: Explore error mitigation and variational methods!")
    `,
      },
    ],
    '1.2.4': {
      section: '1.2.4',
      name: 'Bernstein-Vazirani Algorithm',
      code: `# ================================================================
# 1 Bernstein–Vazirani: find a hidden 4-bit string in ONE query
# ================================================================
import numpy as np

print("🔍 BERNSTEIN-VAZIRANI QUANTUM ALGORITHM")
print("=" * 50)
print("🎯 Goal: Find a hidden 4-bit string in just ONE quantum query!")
print("📚 Classical computers need 4 queries, quantum needs only 1!")

# ---------- 1. Problem set-up -----------------------------------
secret = np.array([1, 0, 1, 1])            # the hidden 4-bit string "s"
n = len(secret)                            # number of input qubits
N = 2**n                                   # size of the search space

print(f"\\n1️⃣  PROBLEM SETUP:")
print(f"    🔒 Hidden secret string: s = {secret} (binary: {''.join(map(str, secret))})")
print(f"    📏 String length: n = {n} bits")
print(f"    🌌 Search space: 2^{n} = {N} possible strings")
print(f"    🎰 Classical brute force: up to {n} queries needed")
print(f"    ⚡ Quantum advantage: exactly 1 query!")

# Basis |0…0> represented as a length-N column vector.
state = np.zeros(N, complex)
state[0] = 1
print(f"\\n    Initial state: |0000⟩ = {state[:4]}... (all amplitude on |0000⟩)")

# ---------- 2. Hadamards create uniform superposition -----------
H = (1/np.sqrt(2))*np.array([[1, 1],
                            [1,-1]], complex)

def hadamards_on_n(n):
    """Return H⊗n as a 2**n × 2**n matrix."""
    U = H
    for _ in range(n-1):
        U = np.kron(U, H)
    return U

print(f"\\n2️⃣  SUPERPOSITION CREATION:")
print(f"    🌊 Apply H⊗{n} (Hadamard on each qubit)")
print(f"    📐 Creates uniform superposition: (1/√{N}) Σ|x⟩")

state = hadamards_on_n(n) @ state          # |s> ← H⊗n |0…0>
print(f"    ✅ All {N} states now have equal amplitude: {abs(state[0]):.3f}")

# ---------- 3. Oracle: add a phase (-1)^{s·x} -------------------
print(f"\\n3️⃣  ORACLE QUERY (THE MAGIC!):")
print(f"    🔮 Oracle knows secret s = {secret}")
print(f"    ⚡ Oracle applies phase (-1)^(s·x) to each |x⟩")
print(f"    📊 Phase flip when s·x = 1 (mod 2)")

phase_flips = 0
# The oracle is diagonal; easiest is to multiply each amplitude.
for x in range(N):
    # Convert x to binary bits (big-endian to match secret format)
    x_bits = [int(b) for b in f"{x:0{n}b}"]
    dot_product = sum(x_bits[i] * secret[i] for i in range(n)) % 2
    if dot_product == 1:
        state[x] *= -1                     # flip phase when s·x = 1
        phase_flips += 1

print(f"    🔄 Phase flipped on {phase_flips}/{N} computational basis states")

# ---------- 4. Second layer of Hadamards ------------------------
print(f"\\n4️⃣  INTERFERENCE MAGIC:")
print(f"    🌊 Apply H⊗{n} again to create interference")
print(f"    ⚡ Constructive interference concentrates amplitude on |s⟩")
print(f"    💫 Destructive interference cancels other states")

state = hadamards_on_n(n) @ state

# ---------- 5. Measure: the amplitudes encode the answer --------
print(f"\\n5️⃣  MEASUREMENT & EXTRACTION:")
probabilities = abs(state)**2
answer = np.argmax(probabilities)          # index of biggest prob.
answer_bits = [int(b) for b in f"{answer:0{n}b}"]

print(f"    📊 Measurement probabilities:")
for i in range(min(16, N)):  # Show all 16 states for 4-bit case
    bits = f"{i:0{n}b}"
    prob = probabilities[i]
    marker = " ⭐" if i == answer else ""
    if prob > 1e-10:  # Only show non-zero probabilities
        print(f"    |{bits}⟩: {prob:.6f}{marker}")

print(f"\\n🎯 ALGORITHM RESULT:")
print(f"    🔍 Measured state: |{answer:0{n}b}⟩")
print(f"    📤 Extracted bits: {answer_bits}")
print(f"    🔒 Hidden string:  {list(secret)}")

# Verification
success = answer_bits == list(secret)
if success:
    print(f"    ✅ SUCCESS! Found the secret in just 1 query!")
    print(f"    📈 Probability of success: {probabilities[answer]:.6f}")
else:
    print(f"    ❌ Something went wrong...")
    print(f"    🔧 Debug: Expected decimal {int(''.join(map(str, secret)), 2)}, got {answer}")

print(f"\\n💡 QUANTUM SPEEDUP ACHIEVED:")
print(f"    🐌 Classical: needs up to {n} queries (ask about each bit)")
print(f"    🚀 Quantum: needs exactly 1 query (parallel superposition)")
print(f"    📊 Speedup factor: {n}x faster!")

print(f"\\n🧠 How it works:")
print(f"    • Superposition queries ALL {N} inputs simultaneously")
print(f"    • Oracle phase-marks states based on secret dot product") 
print(f"    • Interference amplifies correct answer, cancels wrong ones")
print(f"    • Measurement extracts the secret with high probability!")
    `,
    },
    '1.2.5': {
      section: '1.2.5',
      name: 'Two-Qubit Grover Search',
      code: `import numpy as np

print("🔍 GROVER'S QUANTUM SEARCH ALGORITHM")
print("=" * 45)
print("🎯 Goal: Find 1 marked item out of 4 unsorted items")
print("📈 Quantum advantage: √N speedup over classical!")

# ---------- 1. Database & oracle --------------------------------
marked = 2                                # we'll mark |10> (=decimal 2)
N = 4

print(f"\\n1️⃣  SEARCH PROBLEM SETUP:")
print(f"    🎲 Database size: N = {N} items")
print(f"    🏁 Marked item: index {marked} = |{marked:02b}⟩ = |10⟩")
print(f"    🎰 Classical search: needs ~{N//2:.1f} queries on average")
print(f"    ⚡ Grover's algorithm: needs ~{int(np.sqrt(N))} iterations!")

# Uniform superposition on 2 qubits
state = np.ones(N, complex) / np.sqrt(N)
print(f"\\n    Initial superposition: |ψ₀⟩ = (1/2)(|00⟩ + |01⟩ + |10⟩ + |11⟩)")
print(f"    All items equally likely: {abs(state[0])**2:.3f} each")

# Diffusion operator D = 2|s><s| − I
s = np.ones((N, 1), complex) / np.sqrt(N)
D = 2 * (s @ s.T.conj()) - np.eye(N)

print(f"\\n2️⃣  GROVER OPERATORS:")
print(f"    🔮 Oracle O: flips phase of marked item |{marked:02b}⟩")
print(f"    🌊 Diffusion D: reflects about average amplitude")
print(f"    🔄 One Grover iteration: G = D·O")

# Helper: oracle matrix that flips the phase of |marked>
O = np.eye(N, dtype=complex)
O[marked, marked] = -1

def print_state_analysis(state, iteration):
    """Pretty print the quantum state with probabilities."""
    probabilities = abs(state)**2
    print(f"\\n    📊 State after {iteration} iterations:")
    for i in range(N):
        amplitude = state[i]
        prob = probabilities[i]
        marker = " 🎯" if i == marked else ""
        print(f"    |{i:02b}⟩: amplitude = {amplitude.real:+.3f}{amplitude.imag:+.3f}j, P = {prob:.4f}{marker}")
    
    marked_prob = probabilities[marked]
    print(f"    ➡️  P(marked item) = {marked_prob:.4f} = {marked_prob*100:.1f}%")

# Show initial state
print_state_analysis(state, 0)

# ---------- 2. Grover iteration (do it twice) -------------------
def grover_step(ψ):
    return D @ (O @ ψ)

optimal_iterations = int(np.pi/4 * np.sqrt(N))
print(f"\\n3️⃣  GROVER ITERATIONS:")
print(f"    🎯 Optimal iterations for N={N}: ~π√N/4 ≈ {optimal_iterations}")
print(f"    🔄 Running {optimal_iterations} iterations...")

for it in range(optimal_iterations):
    # Apply oracle first
    state_after_oracle = O @ state
    oracle_marked_prob = abs(state_after_oracle[marked])**2
    print(f"\\n    Iteration {it+1}:")
    print(f"    🔮 After Oracle: P(marked) = {oracle_marked_prob:.4f}")
    
    # Then apply diffusion
    state = D @ state_after_oracle
    marked_prob = abs(state[marked])**2
    print(f"    🌊 After Diffusion: P(marked) = {marked_prob:.4f}")

print_state_analysis(state, optimal_iterations)

# ---------- 3. "Measure" (pick max amplitude) -------------------
probabilities = abs(state)**2
result = np.argmax(probabilities)
success_prob = probabilities[marked]

print(f"\\n4️⃣  MEASUREMENT & RESULT:")
print(f"    🎲 Measuring the quantum register...")
print(f"    📤 Most likely outcome: |{result:02b}⟩ (index {result})")
print(f"    🎯 Target was: |{marked:02b}⟩ (index {marked})")

if result == marked:
    print(f"    ✅ SUCCESS! Found the marked item!")
    print(f"    📈 Success probability: {success_prob:.4f} = {success_prob*100:.1f}%")
else:
    print(f"    ❌ Failed this time (but algorithm is probabilistic)")
    print(f"    📈 Success probability was: {success_prob:.4f} = {success_prob*100:.1f}%")

print(f"\\n💡 QUANTUM SPEEDUP ANALYSIS:")
print(f"    🐌 Classical search: O(N) = O({N}) queries")
print(f"    ⚡ Grover search: O(√N) = O({int(np.sqrt(N))}) iterations")
print(f"    📊 Speedup factor: {N/int(np.sqrt(N)):.1f}x faster!")
print(f"    🚀 For large databases: quadratic speedup!")

print(f"\\n🧠 How Grover's algorithm works:")
print(f"    • Start with uniform superposition (all items equally likely)")
print(f"    • Oracle marks target by flipping its phase")
print(f"    • Diffusion amplifies marked amplitudes, suppresses others")
print(f"    • Repeat ~√N times to maximize success probability")
print(f"    • Measure to extract the marked item with high probability!")

print(f"\\n🌟 This demonstrates quantum search advantage!")
print(f"   For N = 1,000,000 items: Classical ~500k vs Grover ~1k!")
    `,
    },
    '1.2.6': {
      section: '3.2.6',
      name: 'Tiny Shor Period-Finder',
      code: `# ================================================================
# Tiny "Shor" Period-Finder: a^x mod 15, n=3 qubits
# We look for period r of a=7 → 7^x mod 15
# ================================================================
import numpy as np
import fractions
import math

print("🔐 SHOR'S FACTORING ALGORITHM (Simplified Demo)")
print("=" * 50)
print("🎯 Goal: Factor N = 15 using period finding")
print("💎 The algorithm that could break RSA encryption!")

# ---------- 1. Problem Setup --------------------------------
N_to_factor = 15
a = 7

print(f"\\n1️⃣  FACTORING PROBLEM:")
print(f"    🎯 Target number to factor: N = {N_to_factor}")
print(f"    🎲 Chosen random base: a = {a}")
print(f"    ✅ Check: gcd({a}, {N_to_factor}) = {math.gcd(a, N_to_factor)} (must be 1)")
print(f"    🔍 Goal: Find period r of the function f(x) = {a}^x mod {N_to_factor}")

# ---------- 2. Classical oracle table (period finding) ----------
print(f"\\n2️⃣  PERIOD FINDING (Classical Brute Force):")
print(f"    📊 Computing powers of {a} modulo {N_to_factor}:")

table = np.array([pow(a, x, N_to_factor) for x in range(8)])
print("    x:    ", " ".join([f"{x:2d}" for x in range(8)]))
print(f"    {a}^x: ", " ".join([f"{val:2d}" for val in table]))

# Find the period
period_found = False
for r in range(1, 8):
    if table[r] == 1:
        print(f"\\n    🎯 PERIOD FOUND: r = {r}")
        print(f"    ✅ Verification: {a}^{r} ≡ {table[r]} ≡ 1 (mod {N_to_factor})")
        print(f"    🔄 Pattern repeats every {r} steps: {table[:r+1]}")
        period_found = True
        break

if not period_found:
    print(f"    ❌ No period found in range [1,7]")
    exit()

# ---------- 3. Classical → Quantum advantage explanation --------
print(f"\\n3️⃣  QUANTUM VS CLASSICAL:")
print(f"    🐌 Classical approach: Try all x values (exponential time)")
print(f"    ⚡ Real Shor's algorithm uses:")
print(f"       • Quantum superposition: |x⟩ → Σ|x⟩ (all x at once!)")
print(f"       • Quantum oracle: |x,0⟩ → |x, {a}^x mod {N_to_factor}⟩")
print(f"       • Quantum Fourier Transform: extract period from phases")
print(f"    📈 Quantum speedup: Exponential → Polynomial time!")

# ---------- 4. From period to factors (the magic!) --------------
print(f"\\n4️⃣  EXTRACTING FACTORS FROM PERIOD:")
print(f"    🔑 Key insight: If {a}^r ≡ 1 (mod {N_to_factor}), then:")
print(f"       {a}^r - 1 ≡ 0 (mod {N_to_factor})")
print(f"       ({a}^(r/2))² - 1 ≡ 0 (mod {N_to_factor})")
print(f"       ({a}^(r/2) - 1)({a}^(r/2) + 1) ≡ 0 (mod {N_to_factor})")

if r % 2 == 0:
    half_power = pow(a, r//2, N_to_factor)
    print(f"\\n    📐 Since r = {r} is even:")
    print(f"    🧮 {a}^({r}//2) = {a}^{r//2} ≡ {half_power} (mod {N_to_factor})")
    
    factor1_candidate = half_power - 1
    factor2_candidate = half_power + 1
    print(f"    📊 Candidates: ({half_power} - 1) = {factor1_candidate}, ({half_power} + 1) = {factor2_candidate}")
    
    p = math.gcd(factor1_candidate, N_to_factor)
    q = math.gcd(factor2_candidate, N_to_factor)
    
    print(f"\\n    🔍 Factor extraction:")
    print(f"    gcd({factor1_candidate}, {N_to_factor}) = {p}")
    print(f"    gcd({factor2_candidate}, {N_to_factor}) = {q}")
    
    print(f"    🔧 Debug: p={p}, q={q}, N={N_to_factor}")
    print(f"    🔧 Condition check: p>1={p>1}, q>1={q>1}, p≠N={p!=N_to_factor}, q≠N={q!=N_to_factor}")
    
    if p > 1 and q > 1 and p != N_to_factor and q != N_to_factor:
        print(f"\\n🎉 SUCCESS! FACTORIZATION FOUND:")
        print(f"    {N_to_factor} = {p} × {q}")
        print(f"    ✅ Verification: {p} × {q} = {p * q}")
        
        # Check if they're prime
        def is_prime(n):
            if n < 2: return False
            for i in range(2, int(n**0.5) + 1):
                if n % i == 0: return False
            return True
        
        print(f"    🔸 {p} is {'prime' if is_prime(p) else 'composite'}")
        print(f"    🔸 {q} is {'prime' if is_prime(q) else 'composite'}")
        
    else:
        print(f"    ❌ Failed to extract non-trivial factors")
        print(f"    💡 In practice, try different values of 'a'")
        
else:
    print(f"\\n    ❌ Period r = {r} is odd, cannot use this method")
    print(f"    💡 In practice, try different values of 'a'")

# ---------- 5. Real-world implications --------------------------
print(f"\\n💡 REAL-WORLD IMPLICATIONS:")
print(f"    🔐 RSA encryption relies on difficulty of factoring large numbers")
print(f"    🚨 Numbers like N = 15 (toy example)")
print(f"    💥 Real RSA uses N with 1000+ digits (2048+ bits)")
print(f"    ⚡ Quantum computer + Shor's algorithm → breaks RSA!")

print(f"\\n🧮 COMPUTATIONAL COMPLEXITY:")
print(f"    🐌 Best classical factoring: ~exp(∛(log N)) - super exponential!")
print(f"    ⚡ Shor's quantum algorithm: ~(log N)³ - polynomial!")
print(f"    📊 For 2048-bit RSA: Classical ~10^140 years vs Quantum ~hours")

print(f"\\n🌟 Why Shor's algorithm is revolutionary:")
print(f"    • Quantum superposition enables parallel period finding")
print(f"    • QFT extracts hidden periodicities efficiently")
print(f"    • Pure math: period finding → factoring")
print(f"    • Threatens current cryptography → drives quantum research!")

print(f"\\n⚠️  Note: This demo uses classical period finding for education.")
print(f"    Real Shor's algorithm uses quantum superposition + QFT!")
    `,
    },
    '1.2.7': [
      {
        section: '1.2.7',
        name: 'One-Qubit VQE',
        code: `# ================================================================
# One-Qubit VQE: minimise ⟨ψ(θ)| Z |ψ(θ)⟩
# ================================================================
import numpy as np
import random, math

print("🧪 VARIATIONAL QUANTUM EIGENSOLVER (VQE)")
print("=" * 50)
print("🎯 Goal: Find ground state of Hamiltonian H = Z")
print("🔬 Hybrid quantum-classical optimization for chemistry!")

# ---------- 1. Problem Setup ---------------------------------
Z = np.array([[1,0],[0,-1]], complex)

print(f"\\n1️⃣  QUANTUM CHEMISTRY PROBLEM:")
print(f"    🔬 Hamiltonian: H = Z (Pauli-Z matrix)")
print(f"    📊 H = [[+1,  0], [0, -1]]")
print(f"    🎯 Eigenvalues: λ₁ = +1 (excited), λ₂ = -1 (ground)")
print(f"    🌟 Eigenstates: |0⟩ (excited), |1⟩ (ground)")
print(f"    🔍 Goal: Find θ such that ⟨ψ(θ)|H|ψ(θ)⟩ is minimized")

def psi(theta):
    """Prepare |ψ(θ)> = cos(θ/2)|0> + sin(θ/2)|1>"""
    return np.array([math.cos(theta/2),
                    math.sin(theta/2)], complex)

def expectation(theta):
    """Compute ⟨ψ(θ)|Z|ψ(θ)⟩"""
    v = psi(theta)
    return np.real(np.vdot(v, Z @ v))

print(f"\\n2️⃣  PARAMETERIZED QUANTUM STATE:")
print(f"    🌊 Ansatz: |ψ(θ)⟩ = cos(θ/2)|0⟩ + sin(θ/2)|1⟩")
print(f"    🎛️  Single parameter: θ (rotation angle)")
print(f"    📐 State space: Bloch sphere parameterization")

# Show a few example states
test_angles = [0, math.pi/4, math.pi/2, 3*math.pi/4, math.pi]
print(f"\\n    📊 Example states:")
for angle in test_angles:
    state = psi(angle)
    exp_val = expectation(angle)
    print(f"    θ = {angle/math.pi:.2f}π: |ψ⟩ = {state[0].real:+.3f}|0⟩ {state[1].real:+.3f}|1⟩, ⟨Z⟩ = {exp_val:+.3f}")

# ---------- 3. Classical Optimization Loop -----------------
print(f"\\n3️⃣  CLASSICAL OPTIMIZATION:")
print(f"    🎲 Random initialization...")

theta = random.random() * 2*math.pi
initial_theta = theta
initial_energy = expectation(theta)

print(f"    🎯 Starting: θ = {theta:.3f} ({theta/math.pi:.2f}π)")
print(f"    ⚡ Initial energy: ⟨Z⟩ = {initial_energy:+.3f}")

lr = 0.2                                 # crude learning-rate
print(f"    🔧 Learning rate: {lr}")
print(f"    📈 Using finite-difference gradient descent")

print(f"\\n4️⃣  OPTIMIZATION PROGRESS:")
print(f"    Step   θ        θ/π      ⟨Z⟩     Gradient")
print(f"    " + "-"*45)

for step in range(60):
    # Finite-difference gradient
    grad = (expectation(theta+0.01) - expectation(theta-0.01)) / 0.02
    theta -= lr * grad
    
    if step % 10 == 0:
        energy = expectation(theta)
        print(f"    {step:2d}     {theta:.3f}    {theta/math.pi:.3f}    {energy:+.3f}     {grad:+.3f}")

final_energy = expectation(theta)
final_state = psi(theta)

print(f"\\n5️⃣  OPTIMIZATION RESULT:")
print(f"    🎯 Final θ: {theta:.3f} ({theta/math.pi:.2f}π)")
print(f"    ⚡ Final energy: ⟨Z⟩ = {final_energy:+.3f}")
print(f"    🌊 Final state: |ψ⟩ = {final_state[0].real:+.3f}|0⟩ {final_state[1].real:+.3f}|1⟩")

# ---------- 6. Analysis and Verification -------------------
theoretical_ground = -1.0
error = abs(final_energy - theoretical_ground)
convergence = abs(theta - math.pi)

print(f"\\n6️⃣  VERIFICATION:")
print(f"    🎯 Theoretical ground state: |1⟩ at θ = π")
print(f"    🔬 Theoretical ground energy: {theoretical_ground:+.3f}")
print(f"    📊 Energy error: |{final_energy:.3f} - ({theoretical_ground})| = {error:.6f}")
print(f"    📐 Parameter error: |θ - π| = {convergence:.6f}")

if error < 0.01:
    print(f"    ✅ SUCCESS! Found ground state within 1% accuracy!")
else:
    print(f"    ⚠️  Reasonable approximation (might need more iterations)")

print(f"\\n💡 VQE ALGORITHM SUMMARY:")
print(f"    🔄 Hybrid quantum-classical optimization")
print(f"    🌊 Quantum computer: Prepare |ψ(θ)⟩ and measure ⟨H⟩")
print(f"    🧮 Classical computer: Optimize θ to minimize ⟨H⟩")
print(f"    🔁 Iterate until convergence")

print(f"\\n🚀 REAL-WORLD APPLICATIONS:")
print(f"    💊 Drug discovery (molecular ground states)")
print(f"    🔋 Battery materials (electronic structure)")
print(f"    🌱 Catalyst design (chemical reactions)")
print(f"    🧪 Quantum chemistry (many-body problems)")

print(f"\\n📈 QUANTUM ADVANTAGE:")
print(f"    🐌 Classical: exponential scaling with system size")
print(f"    ⚡ VQE: polynomial scaling on quantum hardware")
print(f"    🎯 NISQ-era algorithm (works on noisy quantum computers)")

improvement = initial_energy - final_energy
print(f"\\n🎉 Energy improvement: {improvement:+.3f} (better = more negative)")
print(f"    Started: {initial_energy:+.3f} → Finished: {final_energy:+.3f}")

print(f"\\n🌟 VQE is a cornerstone of quantum computing applications!")
print(f"    It bridges quantum hardware with real-world chemistry problems.")
      `,
      },
      {
        section: '1.2.7',
        name: 'QAOA Max-Cut on 3-Node Ring',
        code: `# ================================================================
# QAOA Max-Cut on a 3-Node Ring (p=1)
# ================================================================
import numpy as np
import math
from scipy.linalg import expm

print("🔪 QAOA: QUANTUM APPROXIMATE OPTIMIZATION ALGORITHM")
print("=" * 55)
print("🎯 Goal: Solve Max-Cut problem on 3-node ring graph")
print("🏗️ NISQ-era algorithm for combinatorial optimization!")

# ---------- 1. Problem graph ------------------------------------
# Edges: (0-1), (1-2), (2-0)  → ring of 3 nodes
edge_list = [(0,1),(1,2),(2,0)]
n = 3
N = 2**n

print(f"\\n1️⃣  MAX-CUT PROBLEM SETUP:")
print(f"    📊 Graph: 3-node ring")
print(f"    🔗 Edges: {edge_list}")
print(f"    ✂️  Goal: Find cut that maximizes edges between partitions")
print(f"    🎲 Search space: 2^{n} = {N} possible cuts")

# Show all possible cuts
print(f"\\n    📈 All possible cuts:")
print(f"    Cut (binary)  Partition A  Partition B  Edges Cut  Energy")
print(f"    " + "-"*55)

def count_cut_edges(partition):
    """Count edges cut by a given partition."""
    cut_count = 0
    for i, j in edge_list:
        if partition[i] != partition[j]:  # Different partitions
            cut_count += 1
    return cut_count

for cut in range(N):
    binary = f"{cut:03b}"
    partition = [int(b) for b in binary]
    part_A = [i for i, p in enumerate(partition) if p == 0]
    part_B = [i for i, p in enumerate(partition) if p == 1]
    edges_cut = count_cut_edges(partition)
    energy = -edges_cut  # Negative because we minimize in QAOA
    
    print(f"    {binary}           {part_A}        {part_B}        {edges_cut}         {energy}")

Z = np.array([[1,0],[0,-1]], complex)

def kron_n(*mats):
    """Kronecker product of list of matrices."""
    M = np.array([[1]])
    for m in mats:
        M = np.kron(M, m)
    return M

# ---------- 2. Build cost Hamiltonian ---------------------------
print(f"\\n2️⃣  COST HAMILTONIAN CONSTRUCTION:")
print(f"    🔬 H_C = Σ (1 - Z_i Z_j) / 2  (over all edges)")
print(f"    💡 Minimizing H_C maximizes cut edges")
print(f"    📐 Z_i Z_j = +1 (same), -1 (different)")

H_C = np.zeros((N,N), complex)
for (i,j) in edge_list:
    ZiZj = kron_n(*(Z if k in (i,j) else np.eye(2) for k in range(n)))
    H_C += -0.5 * (np.eye(N) - ZiZj)      # NEGATIVE to maximize cuts!
    print(f"    Edge ({i},{j}): contributes -(1 - Z_{i}Z_{j})/2")

print(f"\\n    📊 Cost Hamiltonian eigenvalues:")
eigenvals = np.sort(np.real(np.linalg.eigvals(H_C)))
for i, val in enumerate(eigenvals):
    marker = " ⭐ (optimal!)" if abs(val - eigenvals[0]) < 1e-10 else ""
    print(f"    λ_{i} = {val:.3f}{marker}")

# ---------- 3. Mixer Hamiltonian (sum of X) ---------------------
print(f"\\n3️⃣  MIXER HAMILTONIAN:")
print(f"    🌊 H_M = Σ X_i  (sum of Pauli-X on each qubit)")
print(f"    💫 Creates quantum superposition between cuts")

X = np.array([[0,1],[1,0]], complex)
H_M = sum(kron_n(*(X if k==i else np.eye(2) for k in range(n)))
            for i in range(n))

print(f"    📐 X_i flips qubit i: |0⟩ ↔ |1⟩")
print(f"    🔄 Enables exploration of cut space")

# ---------- 4. QAOA Algorithm Explanation -----------------------
print(f"\\n4️⃣  QAOA ALGORITHM STRUCTURE:")
print(f"    🔄 Quantum-classical hybrid optimization")
print(f"    📊 Depth p = 1 (one layer)")
print(f"    🎛️  Parameters: γ (cost), β (mixer)")
print(f"    ")
print(f"    Algorithm steps:")
print(f"    1️⃣  |+⟩^⊗{n} ← equal superposition start")
print(f"    2️⃣  U_C(γ) = exp(-iγH_C) ← cost evolution")
print(f"    3️⃣  U_M(β) = exp(-iβH_M) ← mixer evolution")
print(f"    4️⃣  Measure ⟨H_C⟩ ← energy expectation")
print(f"    5️⃣  Optimize γ, β classically")

# ---------- 5. Parameter sweep (coarse grid) --------------------
print(f"\\n5️⃣  PARAMETER OPTIMIZATION:")
print(f"    🔍 Grid search over γ ∈ [0,π], β ∈ [0,π]")

gammas = np.linspace(0, math.pi, 40)
betas  = np.linspace(0, math.pi, 40)
best_E, best_params = 1e9, (0,0)

print(f"    🎯 Searching {len(gammas)} × {len(betas)} = {len(gammas)*len(betas)} parameter combinations...")

energy_landscape = []
for γ in gammas:
    for β in betas:
        # Prepare |+++>
        state = np.ones(N, complex) / np.sqrt(N)
        # Apply cost unitary U_C = exp(-i γ H_C)
        state = expm(-1j*γ*H_C) @ state
        # Apply mixer unitary U_B
        state = expm(-1j*β*H_M) @ state
        E = np.real(np.vdot(state, H_C @ state))
        energy_landscape.append(E)
        if E < best_E:
            best_E, best_params = E, (γ, β)

optimal_cut_edges = -best_E
max_possible_cuts = 2  # For 3-ring, maximum is 2 edges
approximation_ratio = optimal_cut_edges / max_possible_cuts if max_possible_cuts > 0 else 0

print(f"\\n6️⃣  OPTIMIZATION RESULTS:")
print(f"    🎯 Optimal parameters: γ = {best_params[0]:.3f}, β = {best_params[1]:.3f}")
print(f"    ⚡ Minimum energy: {best_E:.3f}")
print(f"    ✂️  Expected cut edges: {optimal_cut_edges:.3f}")
print(f"    📊 Approximation ratio: {approximation_ratio:.3f} (1.0 = perfect)")

# Debug: Check what state QAOA actually found
print(f"\\n🔧 DEBUG ANALYSIS:")
final_state = np.ones(N, complex) / np.sqrt(N)
final_state = expm(-1j*best_params[0]*H_C) @ final_state
final_state = expm(-1j*best_params[1]*H_M) @ final_state
final_probs = abs(final_state)**2

print(f"    🎲 Final state probabilities:")
for i in range(N):
    binary = f"{i:03b}"
    partition = [int(b) for b in binary]
    edges_cut = count_cut_edges(partition)
    prob = final_probs[i]
    if prob > 0.01:  # Only show significant probabilities
        print(f"    |{binary}⟩: P = {prob:.4f}, cuts {edges_cut} edges")

if approximation_ratio > 0.9:
    print(f"    ✅ EXCELLENT! Near-optimal solution found!")
elif approximation_ratio > 0.7:
    print(f"    ✅ GOOD! High-quality approximate solution!")
elif approximation_ratio > 0.1:
    print(f"    ⚠️  Poor performance - algorithm needs improvement")
else:
    print(f"    ❌ ALGORITHM FAILURE! Finding worst solutions")
    print(f"    💡 Possible issues: Hamiltonian, parameters, or implementation bug")

# ---------- 6. Analysis and Applications ------------------------
print(f"\\n💡 QAOA ALGORITHM INSIGHTS:")
print(f"    🎯 NISQ-era algorithm (works on current quantum hardware)")
print(f"    🔄 Hybrid: quantum state evolution + classical optimization")
print(f"    📈 Deeper circuits (larger p) → better approximations")
print(f"    ⚡ Polynomial quantum speedup for some problems")

print(f"\\n🚀 REAL-WORLD APPLICATIONS:")
print(f"    🏭 Supply chain optimization")
print(f"    📡 Network design and partitioning")
print(f"    💰 Portfolio optimization")
print(f"    🧬 Protein folding")
print(f"    📊 Machine learning clustering")

print(f"\\n📊 PERFORMANCE ANALYSIS:")
print(f"    🎲 Problem size: {n} qubits, {N} configurations")
print(f"    🔍 Parameters explored: {len(energy_landscape)}")
print(f"    📈 Energy range: [{min(energy_landscape):.3f}, {max(energy_landscape):.3f}]")

classical_optimum = min([-count_cut_edges([int(b) for b in f"{cut:03b}"]) for cut in range(N)])
print(f"    🏆 Classical optimum: {classical_optimum:.3f}")
print(f"    ⚡ QAOA result: {best_E:.3f}")
print(f"    🎯 Gap: {abs(best_E - classical_optimum):.6f}")

print(f"\\n🌟 QAOA demonstrates quantum advantage for optimization!")
print(f"    It's a leading candidate for near-term quantum applications.")
      `,
      },
    ],
  },
};
